<template>
    <div class="doughnut-chart-container pt-5 pr-5">
        <Doughnut
        :chart-options="chartOptions"
        :chart-data="chartData"
        :chart-id="chartId"
        :dataset-id-key="datasetIdKey"
        :plugins="[customConnectorPlugin, ...plugins]"
        :css-classes="cssClasses"
        :width="width"
        :height="height"
        />
    </div>
</template>

<script>
import { Doughnut } from 'vue-chartjs'

import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement, CategoryScale } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale, ChartDataLabels)

export default {
  name: 'DoughnutChart',
  components: {
    Doughnut
  },
  props: {
    chartId: {
      type: String,
      default: 'doughnut-chart'
    },
    datasetIdKey: {
      type: String,
      default: 'label'
    },
    width: {
      type: Number,
      default: undefined
    },
    height: {
      type: Number,
      default: undefined
    },
    cssClasses: {
      default: '',
      type: String
    },
    styles: {
      type: Object,
      default: () => {}
    },
    plugins: {
      type: Array,
      default() {
        return [];
      }
    },
    decisionData: {
      type: Array,
      required: true
    },
    decisionDataInPercent: {
      type: Array,
      required: true
    }
  },
  computed: {
  chartData() {
    return {
    labels: ['Pending with Hiring Manager', 'Decision Done by Hiring Manager','Pending with HR', 'Pending with Interviewer'],
        datasets: [
          {
            backgroundColor: ['#AC9EE4', '#9EC9FF', '#FBC493', '#FF78A0'],
            data: this.decisionDataInPercent
          }
        ],
      }
    }
  },
  mounted() {
    this.updateFontSizeForBreakpoint();
    window.addEventListener('resize', this.updateFontSizeForBreakpoint);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateFontSizeForBreakpoint);
  },
  methods: {
    updateFontSizeForBreakpoint() {
      const width = window.innerWidth;
      const isSmall = width <= 1380;

      if (width <= 1478) {
      // Smaller screens
      this.lineLength = 30;
      this.smallOffset = 25;
    } else if (width <= 1848) {
      // Medium screens
      this.lineLength = 10;
      this.smallOffset = 3;
    } else {
      // Large screens
      this.lineLength = 10;
      this.smallOffset = 3;
    }
    

      // Update chartOptions plugin font sizes
      this.chartOptions.plugins.legend.labels.font.size = isSmall ? 11 : 14;
      this.chartOptions.plugins.datalabels.font.size = isSmall ? 10 : 10;
      
    },
  },
  data() {
    return {
      lineLength: 10, // default
    smallOffset: 3, // default
      
      // Custom plugin to draw connector lines
      customConnectorPlugin: {
        id: 'customConnector',
        afterDatasetsDraw: (chart) => {
          const ctx = chart.ctx;
          const meta = chart.getDatasetMeta(0);
          
          meta.data.forEach((arc, index) => {
            const value = chart.data.datasets[0].data[index];
            
            // Only draw connector for small segments (< 10%)
            if (value < 5 && value > 0) {
              const centerX = arc.x;
              const centerY = arc.y;
              const radius = arc.outerRadius;
              const angle = (arc.startAngle + arc.endAngle) / 2;
              
              // Start point at the edge of the arc
              const startX = centerX + Math.cos(angle) * radius;
              const startY = centerY + Math.sin(angle) * radius;
              
              // End point further out for the label
              
              const endX = centerX + Math.cos(angle) * (radius + this.lineLength);
              const endY = centerY + Math.sin(angle) * (radius + this.lineLength);
              
              // Draw the line
              ctx.save();
              ctx.strokeStyle = '#666';
              ctx.lineWidth = 0.5;
              ctx.beginPath();
              ctx.moveTo(startX, startY);
              ctx.lineTo(endX, endY);
              ctx.stroke();
              ctx.restore();
            }
          });
        }
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
      
        plugins: {
          legend: {
            position: 'right',
            labels: {
              font: {
                size: 7
              },
            
              pointStyle: 'rectRot',
              padding: 28,
              boxWidth: 15,
              boxHeight: 15,
              // Custom label generator for multiline text
              generateLabels: (chart) => {
                const data = chart.data;
                if (data.labels.length && data.datasets.length) {
                  return data.labels.map((label, i) => {
                    const meta = chart.getDatasetMeta(0);
                    const style = meta.controller.getStyle(i);
                    
                    // Split long labels into multiple lines
                    let text = label;
                    const maxCharsPerLine = 22; // Adjust this value as needed
                    
                    if (label.length > maxCharsPerLine) {
                      const words = label.split(' ');
                      const lines = [];
                      let currentLine = '';
                      
                      words.forEach(word => {
                        if ((currentLine + word).length > maxCharsPerLine && currentLine.length > 0) {
                          lines.push(currentLine.trim());
                          currentLine = word + ' ';
                        } else {
                          currentLine += word + ' ';
                        }
                      });
                      
                      if (currentLine.trim().length > 0) {
                        lines.push(currentLine.trim());
                      }
                      
                      text = lines; // Chart.js supports array of strings for multiline
                    }
                    
                    return {
                      text: text,
                      fillStyle: style.backgroundColor,
                      strokeStyle: style.borderColor,
                      lineWidth: style.borderWidth,
                      hidden: !chart.getDataVisibility(i),
                      index: i
                    };
                  });
                }
                return [];
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (tooltipItem) => {
                const label = tooltipItem.label
                const index = tooltipItem.dataIndex
                const percentage = tooltipItem.raw
                
                let count = this.decisionData[index]
                return `${label}: ${percentage}% (${count})`
              }
            }
          },
          datalabels: {
            color: '#000',
            font: {
              size: 2,
              
            },
            formatter: (value) => {
              return value === 0 ? null : `${value}%`;
            },
            // Show labels outside for small segments with lines
            align: (context) => {
              const value = context.dataset.data[context.dataIndex];
              return value < 5 ? 'end' : 'center';
            },
            anchor: (context) => {
              const value = context.dataset.data[context.dataIndex];
              return value < 5 ? 'end' : 'center';
            },
            offset: (context) => {
              const value = context.dataset.data[context.dataIndex];
              return value < 5 ? this.smallOffset : 0;
            },
            // Style labels for small segments
            
          
            padding: 8,
            clip: false,
            // Draw connector lines for small segments
            listeners: {
              enter: function(context) {
                context.hovered = true;
                return true;
              },
              leave: function(context) {
                context.hovered = false;
                return true;
              }
            }
          }
        },
        elements: {
          arc: {
            spacing: 0,  // ✅ Increases space between segments
            borderRadius: 10
          }
        }
      }
    }
  }
}
</script>
<style scoped>
.doughnut-chart-container {
  width: 100%;
  height: 100%;
  max-width: 100%;
  margin: auto;
  position: relative;
}
</style>
